import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, Search, Calendar, ChevronDown, Check, Phone, Users, Stethoscope, Briefcase, Loader2, Download } from 'lucide-react';

// --- Unified Interface for the Table ---
interface UnifiedStaff {
  id: number;
  full_name: string;
  emp_id?: string;
  role_display: string;   // Maps from profile or designation
  contact_display: string; // Every number below, comma-joined, or '-'
  contact_numbers: string[]; // Maps from contacts[] or contact_number_1/2
  staff_status: string;
  designation?: string;    // nurses only — Enum(DesignationEnum)
  clinical_class?: string; // nurses only — Enum(ClinicalClassEnum)
  photo_url?: string;
  last_working_day?: string; // management only — "YYYY-MM-DD"
  type: 'nurse' | 'physio' | 'management';
}

// A row of GET /api/salary-month-summary. Presence in that response is what
// "assigned this month" means — it mirrors the rows the Salary Sheet itself
// renders. The money fields are the sheet's footer, recomputed live; the table
// shows six of them (PAY_COLUMNS) and the Excel export is built from them.
interface MonthSummary {
  staff_id: number;
  deduction_types: string;
  deductions: number;
  gross: number;
  pt: number;
  tds: number;
  total_deductions: number;
  net_take_home: number;
}

type StaffType = 'nurse' | 'physio' | 'management';
type AssignmentFilter = 'assigned' | 'unassigned' | 'all';

const PLACEHOLDER_IMG = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

// --- Editable dropdown option sets (must match the backend enums exactly) ---
// app/models/nurse.py: StaffStatusEnum / DesignationEnum / ClinicalClassEnum.
// A value the DB has but this list doesn't would blank the cell, so
// withCurrent() below always folds the row's own value back in.
// Same status vocabulary as Staff Pool / Daily Board / Nurse Management —
// one status system used everywhere.
const STAFF_STATUS_OPTIONS = [
  'Available', 'On Temporary Duty', 'Back-Up', 'On Duty - Shuffle Possibility',
  'On Leave - Regular', 'On Leave - Emergency', 'On Leave - Sick',
  'Uncertainty', 'Resigned', 'Absconded',
];
const PERMANENT_STATUSES = new Set(['Resigned', 'Absconded', 'Uncertainty']);
const DESIGNATION_OPTIONS = ['ICU Nurse', 'Registered Nurse', 'Staff Nurse', 'Nurse', 'Caregiver'];
const CLINICAL_CLASS_OPTIONS = [
  'Class 1 (Ventilator ICU Nurses)',
  'Class 2 (Tracheostomy ICU Nurses)',
  'Class 3 (Normal Nurses)',
  'Class 4 (Junior Nurses)',
  'Class 1 P (Neonatal & Paediatrics)',
];

// Older records carry values retired from the lists above (e.g. the "Available"
// default the DB still uses). Keep the current value selectable so opening the
// dropdown can never silently rewrite it.
const withCurrent = (options: string[], current?: string) =>
  current && !options.includes(current) ? [current, ...options] : options;

// Column widths, kept here so the per-tab table minimum below stays in step
// with them automatically. Narrowed to make room for the six pay columns, so
// the longer text values (Clinical Class, Status, long names) ellipsise and
// rely on their hover tooltip.
const COL = {
  index: 36,
  name: 190,
  designation: 120,
  clinicalClass: 150,
  role: 95,
  contact: 120,
  status: 130,
  money: 92,
  action: 112,
};

// Clinical Class and Role / Category are hidden at the client's request, not
// removed — set these back to true to restore them in the table and the Excel.
const SHOW_CLINICAL_CLASS = false;
const SHOW_ROLE = false;

// The Salary Sheet's header figures, read from the month summary (each sheet's
// footer, recomputed live). Only the three headline figures are coloured.
const PAY_COLUMNS: { key: keyof MonthSummary; label: string; color: string }[] = [
  { key: 'gross',            label: 'Gross Salary',    color: '#2563eb' },
  { key: 'tds',              label: 'TDS',             color: '#0f172a' },
  { key: 'pt',               label: 'P.T',             color: '#0f172a' },
  { key: 'deductions',       label: 'Advances',        color: '#0f172a' },
  { key: 'total_deductions', label: 'Total Deduction', color: '#dc2626' },
  { key: 'net_take_home',    label: 'Net Salary',      color: '#16a34a' },
];
const PAY_WIDTH = COL.money * PAY_COLUMNS.length;
const CLINICAL_WIDTH = SHOW_CLINICAL_CLASS ? COL.clinicalClass : 0;
const ROLE_WIDTH = SHOW_ROLE ? COL.role : 0;

// Below these the table scrolls rather than truncating. Above them the surplus
// spreads proportionally over every column, so a wide screen no longer dumps
// all its extra space into Staff Name.
const NURSE_TABLE_MIN_WIDTH =
  COL.index + COL.name + COL.designation + CLINICAL_WIDTH + ROLE_WIDTH + COL.contact + COL.status + PAY_WIDTH + COL.action;
const OTHER_TABLE_MIN_WIDTH =
  COL.index + COL.name + ROLE_WIDTH + COL.contact + COL.status + PAY_WIDTH + COL.action;

// Text colour for the nurse Status dropdown: leave in red, available in green.
const statusTextColor = (status: string): string | undefined => {
  if (/leave/i.test(status)) return '#dc2626';
  if (/available/i.test(status)) return '#16a34a';
  return undefined;
};

const getStatusStyle = (status: string) => {
  const s = status?.toLowerCase() || '';
  if (s.includes('available') || s.includes('active')) return { backgroundColor: '#dcfce7', color: '#166534' };
  if (s.includes('leave')) return { backgroundColor: '#fee2e2', color: '#dc2626' };
  if (s.includes('sick')) return { backgroundColor: '#ffedd5', color: '#9a3412' };
  if (s.includes('resigned') || s.includes('absconded') || s.includes('suspended')) return { backgroundColor: '#fee2e2', color: '#991b1b' };
  return { backgroundColor: '#f1f5f9', color: '#475569' };
};

// A select styled to read as plain table text until you interact with it, so
// the table still scans as a table rather than a wall of form controls. All
// three editable columns look identical — the control stays neutral unless a
// `color` is passed (Status uses it to show On Leave in red).
//
// Declared at module level on purpose: defined inside SalarySheetIndex it would
// be a new component type on every render, so React would unmount and remount
// the <select> — closing the dropdown mid-choice every time a sibling row saved.
const EditableCell: React.FC<{
  value: string;
  options: string[];
  placeholder: string;
  busy: boolean;
  onSelect: (value: string) => void;
  color?: string;
}> = ({ value, options, placeholder, busy, onSelect, color }) => (
  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
    <select
      value={value}
      disabled={busy}
      onChange={(e) => onSelect(e.target.value)}
      style={{
        width: '100%',
        appearance: 'none',
        WebkitAppearance: 'none',
        border: '1px solid transparent',
        borderRadius: '6px',
        // Even side padding so the centred text sits clear of the chevron.
        padding: '0.35rem 1.2rem',
        fontSize: '0.82rem',
        fontWeight: 500,
        fontFamily: 'inherit',
        textAlign: 'center',
        textAlignLast: 'center',
        color: value ? (color || '#0f172a') : '#94a3b8',
        backgroundColor: 'transparent',
        cursor: busy ? 'wait' : 'pointer',
        outline: 'none',
        opacity: busy ? 0.55 : 1,
        textOverflow: 'ellipsis',
      }}
      onFocus={(e) => { e.currentTarget.style.border = '1px solid #93c5fd'; }}
      onBlur={(e) => { e.currentTarget.style.border = '1px solid transparent'; }}
      title={value || placeholder}
    >
      <option value="" disabled>{placeholder}</option>
      {withCurrent(options, value).map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
    {busy
      ? <Loader2 size={12} color="#64748b" style={{ position: 'absolute', right: '3px', pointerEvents: 'none', animation: 'spin 1s linear infinite' }} />
      : <ChevronDown size={12} color="#cbd5e1" style={{ position: 'absolute', right: '3px', pointerEvents: 'none' }} />}
  </div>
);

const SalarySheetIndex: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, []);

  // --- Tab State ---
  const [activeTab, setActiveTab] = useState<StaffType>('nurse');

  // Deep-link support: ?tab=management / ?tab=physio from the sidebar shortcuts
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'management' || t === 'physio' || t === 'nurse') {
      setActiveTab(t);
    }
  }, [searchParams]);

  const [staffList, setStaffList] = useState<UnifiedStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchRequestIdRef = useRef(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Live status (nurses only) — same source Staff Pool reads (nurse_availability
  // for today, "On Duty" applied on top for anyone assigned today). Nurse's
  // Status column reads this instead of the dormant staff_status field.
  const [liveStatus, setLiveStatus] = useState<Record<number, string>>({});
  const fetchLiveStatus = useCallback(async () => {
    const today = new Date().toISOString().slice(0, 10);
    try {
      const [availRes, asgnRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/nurse-availability`, { headers: authHeaders(), params: { date: today } }),
        axios.get(`${API_BASE_URL}/api/assignments`, { headers: authHeaders(), params: { start_date: today, end_date: today } }),
      ]);
      const onDutyIds = new Set((asgnRes.data || []).flatMap((a: any) => [a.nurse?.id, a.second_nurse?.id]).filter(Boolean));
      const map: Record<number, string> = {};
      (availRes.data || []).forEach((r: any) => { map[r.nurse_id] = onDutyIds.has(r.nurse_id) ? 'On Duty' : r.category; });
      onDutyIds.forEach((id: any) => { if (!map[id]) map[id] = 'On Duty'; });
      setLiveStatus(map);
    } catch (e) { console.error('fetch live status', e); }
  }, [API_BASE_URL, authHeaders]);

  // --- Month summary state ---
  // Keyed by staff id: presence answers "assigned?", and the row itself is what
  // the Excel export is built from, so exporting needs no extra round-trip.
  const [summary, setSummary] = useState<Record<number, MonthSummary>>({});
  const [summaryLoading, setSummaryLoading] = useState(false);
  // The summary is what powers the Assigned filter, so if it fails to load we
  // fall back to showing everyone rather than an empty-looking table.
  const [summaryFailed, setSummaryFailed] = useState(false);
  const summaryRequestIdRef = useRef(0);

  const [assignmentFilter, setAssignmentFilter] = useState<AssignmentFilter>('assigned');

  // --- Inline edit state ---
  // Which row is mid-save, so the cell can show a spinner and reject a second edit.
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  // --- Export state ---
  const [exporting, setExporting] = useState(false);

  // --- Date Selection State ---
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);
  const monthRef = useRef<HTMLDivElement>(null);
  const yearRef = useRef<HTMLDivElement>(null);

  const months = [ { val: 1, label: 'January' }, { val: 2, label: 'February' }, { val: 3, label: 'March' }, { val: 4, label: 'April' }, { val: 5, label: 'May' }, { val: 6, label: 'June' }, { val: 7, label: 'July' }, { val: 8, label: 'August' }, { val: 9, label: 'September' }, { val: 10, label: 'October' }, { val: 11, label: 'November' }, { val: 12, label: 'December' } ];
  const years = [2024, 2025, 2026, 2027];

  // Management staff are paid off their own daily sheet and have no shift rows,
  // so there is no "assigned this month" to derive for them.
  const supportsAssignment = activeTab !== 'management';

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (monthRef.current && !monthRef.current.contains(event.target as Node)) setIsMonthOpen(false);
      if (yearRef.current && !yearRef.current.contains(event.target as Node)) setIsYearOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch the staff roster whenever the tab changes
  useEffect(() => {
    fetchStaffData(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch the month footprint whenever the tab or the month/year changes
  useEffect(() => {
    fetchMonthSummary(activeTab, selectedMonth, selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedMonth, selectedYear]);

  const fetchStaffData = async (type: StaffType) => {
    // Tag this call so a slower, now-stale request can't overwrite a faster, newer one.
    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);
    setStaffList([]); // Clear current list while loading

    try {
      let endpoint = '';
      if (type === 'nurse') endpoint = '/api/nurses/';
      if (type === 'physio') endpoint = '/api/physiotherapists/';
      if (type === 'management') endpoint = '/api/management-staff/';

      const response = await axios.get(`${API_BASE_URL}${endpoint}`, { headers: authHeaders() });

      // A newer tab switch already started a request after this one — discard this result.
      if (fetchRequestIdRef.current !== requestId) return;

      const rawData = Array.isArray(response.data) ? response.data : (response.data.results || []);

      // Normalize the data so the table can render it uniformly
      const normalizedData: UnifiedStaff[] = rawData.map((item: any) => {
          let role = '-';
          let numbers: (string | undefined)[] = [];
          const contactPhones = (item.contacts || []).map((c: any) => c?.phone_number);

          if (type === 'nurse') {
              role = item.profile || 'Nurse';
              numbers = contactPhones;
          } else if (type === 'physio') {
              role = 'Physiotherapist'; // Physios usually share this universal title
              numbers = [item.contact_number_1, item.contact_number_2, ...contactPhones];
          } else if (type === 'management') {
              role = item.designation || 'Staff';
              numbers = [item.contact_number_1, item.contact_number_2];
          }
          // Every registered number, blanks and repeats dropped.
          const contactNumbers = Array.from(new Set(
            numbers.map(n => (n || '').trim()).filter(Boolean)
          ));

          return {
              id: item.id,
              full_name: item.full_name,
              emp_id: item.emp_id || undefined,
              role_display: role,
              contact_display: contactNumbers.length ? contactNumbers.join(', ') : '-',
              contact_numbers: contactNumbers,
              // Nurses keep the raw value: their status is an editable enum, and
              // inventing an "Active" that StaffStatusEnum doesn't have would put
              // a value in the dropdown the backend rejects. Physios have no
              // status column at all, so the fallback is all they ever show.
              staff_status: type === 'nurse' ? (item.staff_status || '') : (item.staff_status || 'Active'),
              designation: item.designation || undefined,
              clinical_class: item.clinical_class || undefined,
              photo_url: item.photo_url,
              last_working_day: item.last_working_day || undefined,
              type: type
          };
      });

      setStaffList(normalizedData);
      if (type === 'nurse') fetchLiveStatus();
    } catch (err) {
      if (fetchRequestIdRef.current !== requestId) return;
      console.error(`Failed to fetch ${type} data`, err);
    } finally {
      if (fetchRequestIdRef.current === requestId) setLoading(false);
    }
  };

  // Fetched for every tab, management included: management has no "assigned"
  // concept (the filter stays hidden for it), but its Excel register is built
  // from the same response.
  const fetchMonthSummary = async (type: StaffType, month: number, year: number) => {
    const requestId = ++summaryRequestIdRef.current;
    setSummaryLoading(true);
    setSummaryFailed(false);

    try {
      const res = await axios.get(`${API_BASE_URL}/api/salary-month-summary`, {
        headers: authHeaders(),
        params: { staff_type: type, month, year },
      });
      if (summaryRequestIdRef.current !== requestId) return;

      const rows: MonthSummary[] = Array.isArray(res.data) ? res.data : [];
      const byId: Record<number, MonthSummary> = {};
      rows.forEach(r => { byId[r.staff_id] = r; });
      setSummary(byId);
    } catch (err) {
      if (summaryRequestIdRef.current !== requestId) return;
      console.error('Failed to load month summary', err);
      setSummary({});
      setSummaryFailed(true);
    } finally {
      if (summaryRequestIdRef.current === requestId) setSummaryLoading(false);
    }
  };

  // --- INLINE FIELD EDIT ---
  // Optimistic: the cell shows the new value immediately and rolls back if the
  // PATCH fails, so a dropdown never sits frozen waiting on the network.
  const handleInlineEdit = async (staff: UnifiedStaff, field: 'staff_status' | 'designation' | 'clinical_class', value: string) => {
    if (staff.type !== 'nurse') return; // only nurses carry these columns
    const cellKey = `${staff.id}_${field}`;

    // Status — live, from nurse_availability (same as Staff Pool), not the
    // dormant staff_status field. Separate branch: different endpoint, and a
    // confirm guard before overwriting a currently-active permanent status.
    if (field === 'staff_status') {
      const previous = liveStatus[staff.id] || 'Available';
      if (previous === value) return;
      if (PERMANENT_STATUSES.has(previous)) {
        const ok = window.confirm(`This staff is currently marked ${previous}.\n\nDo you want to change their status to "${value || 'Available'}"?`);
        if (!ok) return;
      }
      setSavingCell(cellKey);
      setEditError(null);
      setLiveStatus(prev => ({ ...prev, [staff.id]: value }));
      try {
        const today = new Date().toISOString().slice(0, 10);
        await axios.post(`${API_BASE_URL}/api/nurse-availability`,
          { nurse_id: staff.id, date: today, category: value || 'Available' },
          { headers: authHeaders() });
      } catch (err) {
        console.error('Failed to update status', err);
        setLiveStatus(prev => ({ ...prev, [staff.id]: previous }));
        setEditError(`Could not update ${staff.full_name}. The change was not saved.`);
      } finally {
        setSavingCell(null);
      }
      return;
    }

    const previous = staff[field];
    if (previous === value) return;

    setSavingCell(cellKey);
    setEditError(null);
    setStaffList(prev => prev.map(s => (s.id === staff.id ? { ...s, [field]: value } : s)));

    try {
      await axios.patch(
        `${API_BASE_URL}/api/nurses/${staff.id}/inline`,
        { [field]: value },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
      );
    } catch (err) {
      console.error(`Failed to update ${field}`, err);
      setStaffList(prev => prev.map(s => (s.id === staff.id ? { ...s, [field]: previous } : s)));
      setEditError(`Could not update ${staff.full_name}. The change was not saved.`);
    } finally {
      setSavingCell(null);
    }
  };

  // --- NAVIGATION HANDLER ---
  const handleNavigateToSheet = (staffId: number, staffType: string) => {
    // Management uses its own dedicated daily-grid route; nurses/physios use the shift-based sheet
    navigate(`/salarysheet/${staffType}/${staffId}?month=${selectedMonth}&year=${selectedYear}`);
  };

  const getImageUrl = (path: string | undefined) => {
     if (!path) return PLACEHOLDER_IMG;
     return path.startsWith('http') ? path : `${API_BASE_URL}/${path}`;
  };

  const isAssigned = useCallback(
    (staffId: number) => Boolean(summary[staffId]),
    [summary],
  );

  // A resigned management staff member has no sheet for any month after their
  // last working day (or any month at all, with none recorded) — the same rule
  // as _resigned_for() on the backend, which keeps them out of the Excel
  // register and the P&L too. ISO dates compare correctly as strings.
  const payrollStaff = useMemo(() => {
    if (activeTab !== 'management') return staffList;
    const monthStart = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
    return staffList.filter(s =>
      (s.staff_status || '').trim().toLowerCase() !== 'resigned' ||
      (!!s.last_working_day && s.last_working_day >= monthStart)
    );
  }, [staffList, activeTab, selectedMonth, selectedYear]);

  // Text search first — the assigned/unassigned counts shown on the filter
  // buttons are counts *within the current search*, so they always add up to
  // what the table can actually show.
  //
  // Matches any column the table shows: name, emp ID, designation, contact,
  // status and the six amounts. Hidden columns are left out, so "nurse" doesn't
  // match every row through the hidden Role / Category. A query that is just a
  // number ("₹ 33,600", "33600", "96331 36057") is also compared digit-for-digit
  // against the amounts and phone numbers, ignoring ₹, commas and spaces.
  const searchedStaff = useMemo(() => {
    const q = searchTerm.toLowerCase().replace(/₹/g, '').trim();
    if (!q) return payrollStaff;
    const qDigits = q.replace(/[\s,+-]/g, '');
    const numericQuery = /^\d+$/.test(qDigits);

    return payrollStaff.filter(s => {
      const status = activeTab === 'nurse' ? (liveStatus[s.id] || 'Available') : s.staff_status;
      const text = [
        s.full_name, s.emp_id, s.designation, status,
        SHOW_CLINICAL_CLASS ? s.clinical_class : '',
        SHOW_ROLE ? s.role_display : '',
        ...s.contact_numbers,
      ].join(' | ').toLowerCase();
      if (text.includes(q)) return true;
      if (!numericQuery) return false;

      const m = summary[s.id];
      const numbers = [
        ...s.contact_numbers.map(n => n.replace(/\D/g, '')),
        ...(m ? PAY_COLUMNS.map(c => String(Math.round(Number(m[c.key]) || 0))) : []),
      ];
      return numbers.some(n => n.includes(qDigits));
    });
  }, [payrollStaff, searchTerm, activeTab, liveStatus, summary]);

  const counts = useMemo(() => {
    const assigned = searchedStaff.filter(s => isAssigned(s.id)).length;
    return { assigned, unassigned: searchedStaff.length - assigned, all: searchedStaff.length };
  }, [searchedStaff, isAssigned]);

  const visibleStaff = useMemo(() => {
    // With no usable summary every row would read "unassigned", which would
    // hide the whole table behind a filter the user can't see is broken.
    if (!supportsAssignment || summaryFailed || assignmentFilter === 'all') return searchedStaff;
    if (assignmentFilter === 'assigned') return searchedStaff.filter(s => isAssigned(s.id));
    return searchedStaff.filter(s => !isAssigned(s.id));
  }, [searchedStaff, assignmentFilter, isAssigned, supportsAssignment, summaryFailed]);

  // Management rows carry the Sl.No order set on the Management Staff registration
  // tab and the API already returns them in it, so re-sorting here would throw it
  // away. Nurses and physios have no such order and stay alphabetical.
  const orderedStaff = useMemo(() => (
    activeTab === 'management'
      ? visibleStaff
      : [...visibleStaff].sort((a, b) => {
          const nameA = (a.full_name || '').trim().toLowerCase();
          const nameB = (b.full_name || '').trim().toLowerCase();
          return nameA.localeCompare(nameB);
        })
  ), [visibleStaff, activeTab]);

  // --- Row selection, for the totals cards ---
  // Click a name to select/unselect it; press on a name and drag over other
  // rows to select (or, starting on a selected row, unselect) the whole run.
  // Esc, Clear, or a click anywhere outside the table and the totals cards
  // clears. The cards total the selection, or every row shown when none.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const dragSel = useRef<{ anchor: number; base: Set<number>; add: boolean } | null>(null);
  const tableCardRef = useRef<HTMLDivElement>(null);
  const totalsRef = useRef<HTMLDivElement>(null);
  // The Excel button exports the selection, so pressing it mustn't clear it.
  const exportBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { setSelectedIds(new Set()); }, [activeTab, selectedMonth, selectedYear]);
  useEffect(() => {
    const endDrag = () => { dragSel.current = null; };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectedIds(new Set()); };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (tableCardRef.current?.contains(t) || totalsRef.current?.contains(t) || exportBtnRef.current?.contains(t)) return;
      setSelectedIds(prev => (prev.size ? new Set() : prev));
    };
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('mouseup', endDrag);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, []);
  const selectThrough = (idx: number) => {
    const d = dragSel.current;
    if (!d) return;
    const [lo, hi] = d.anchor <= idx ? [d.anchor, idx] : [idx, d.anchor];
    const next = new Set(d.base);
    orderedStaff.slice(lo, hi + 1).forEach(s => { if (d.add) next.add(s.id); else next.delete(s.id); });
    setSelectedIds(next);
  };
  const startSelect = (idx: number, e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault(); // no text selection while dragging
    dragSel.current = { anchor: idx, base: new Set(selectedIds), add: !selectedIds.has(orderedStaff[idx].id) };
    selectThrough(idx);
  };
  // Only rows still on screen count, so a search or filter change can't leave
  // hidden rows in the totals.
  const selectedRows = orderedStaff.filter(s => selectedIds.has(s.id));
  const totalsRows = selectedRows.length ? selectedRows : orderedStaff;
  const payTotals = PAY_COLUMNS.map(c =>
    totalsRows.reduce((sum, s) => sum + Math.round(Number(summary[s.id]?.[c.key]) || 0), 0));

  const monthLabel = months.find(m => m.val === selectedMonth)?.label || '';

  // Only the nurse tab carries the Designation and Clinical Class columns, so
  // the other tabs need a correspondingly smaller floor.
  const TABLE_MIN_WIDTH = activeTab === 'nurse' ? NURSE_TABLE_MIN_WIDTH : OTHER_TABLE_MIN_WIDTH;

  // --- EXCEL EXPORT ---
  // The payroll register the client signs off: the table's columns, with the
  // same footer figures the corresponding Salary Sheet shows. Honours the
  // search box so a filtered view exports what is on screen, but never the
  // Assigned/Not-assigned toggle: an export of unassigned staff would be a page
  // of zeroes. With rows selected, only those are exported.
  //
  // On nurse/physio a row exists for anyone assigned that month; on management
  // it exists for anyone on payroll that month, saved sheet or not — an unsaved
  // month is reported at the base-salary figures the sheet opens with.
  const exportRows = useMemo(
    () => [...searchedStaff]
      .filter(s => summary[s.id] && (selectedIds.size === 0 || selectedIds.has(s.id)))
      .sort((a, b) => (a.full_name || '').trim().toLowerCase().localeCompare((b.full_name || '').trim().toLowerCase())),
    [searchedStaff, summary, selectedIds],
  );
  // The Status column as the table shows it: nurses read today's live status.
  const statusOf = (s: UnifiedStaff) => (activeTab === 'nurse' ? (liveStatus[s.id] || 'Available') : s.staff_status) || '';

  const handleExportExcel = async () => {
    if (exportRows.length === 0) return;
    setExporting(true);

    try {
      const ExcelJS = (await import('exceljs')).default;
      const { saveAs } = await import('file-saver');

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Salary Register');
      const tabLabel = activeTab === 'physio' ? 'Physiotherapists'
                     : activeTab === 'management' ? 'Management Staff'
                     : 'Nursing Staff';
      // Same columns as the table, in its order. Designation and Clinical
      // Class exist only on the nurse tab.
      const isNurse = activeTab === 'nurse';

      // A blank spacer column so the table starts in column B.
      worksheet.columns = [
        { header: '', key: 'gap', width: 3 },
        { header: 'Sl No', key: 'sl', width: 8 },
        { header: 'Full Name', key: 'name', width: 30 },
        ...(isNurse ? [{ header: 'Designation', key: 'designation', width: 18 }] : []),
        ...(isNurse && SHOW_CLINICAL_CLASS ? [{ header: 'Clinical Class', key: 'clinicalClass', width: 28 }] : []),
        ...(SHOW_ROLE ? [{ header: 'Role / Category', key: 'role', width: 18 }] : []),
        { header: 'Contact', key: 'contact', width: 18 },
        { header: 'Status', key: 'status', width: 22 },
        { header: 'Gross Salary', key: 'gross', width: 16 },
        { header: 'TDS', key: 'tds', width: 14 },
        { header: 'Professional Tax', key: 'pt', width: 18 },
        { header: 'Advances (Amount)', key: 'dedAmt', width: 18 },
        { header: 'Total Deductions', key: 'totalDed', width: 18 },
        { header: 'Net Take Home Salary', key: 'net', width: 22 },
      ];

      const lastCol = worksheet.columns.length;   // spacer + data columns
      const moneyKeys = ['gross', 'tds', 'pt', 'dedAmt', 'totalDed', 'net'];
      const deductionKeys = ['tds', 'pt', 'dedAmt', 'totalDed'];

      // Three blank rows pushes the header from row 1 to row 4, leaving row 1
      // empty as the gap on top and row 2 free for the title.
      worksheet.spliceRows(1, 0, [], [], []);
      worksheet.mergeCells(2, 2, 2, lastCol);
      const titleRow = worksheet.getRow(2);
      titleRow.height = 30;
      titleRow.getCell(2).value = `${tabLabel} - Salary Register - ${monthLabel} ${selectedYear}`;
      titleRow.getCell(2).font = { size: 16, bold: true };
      titleRow.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' };

      const headerRow = worksheet.getRow(4);
      headerRow.height = 25;
      headerRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      // eachCell skips the empty spacer, so borders are applied by index from
      // column 2 onwards — that also keeps column A clean of any styling.
      const styleDataCells = (row: any, fn: (cell: any) => void) => {
        for (let c = 2; c <= lastCol; c++) fn(row.getCell(c));
      };

      exportRows.forEach((staff, i) => {
        const m = summary[staff.id];
        const status = statusOf(staff);
        const row = worksheet.addRow({
          sl: i + 1,
          name: staff.full_name,
          ...(isNurse ? { designation: staff.designation || '' } : {}),
          ...(isNurse && SHOW_CLINICAL_CLASS ? { clinicalClass: staff.clinical_class || '' } : {}),
          ...(SHOW_ROLE ? { role: staff.role_display !== '-' ? staff.role_display : '' } : {}),
          contact: staff.contact_display !== '-' ? staff.contact_display : '',
          status,
          gross: Math.round(m.gross),
          tds: Math.round(m.tds),
          pt: Math.round(m.pt),
          dedAmt: Math.round(m.deductions),
          totalDed: Math.round(m.total_deductions),
          net: Math.round(m.net_take_home),
        });
        styleDataCells(row, cell => {
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          cell.alignment = { vertical: 'middle' };
        });
        row.getCell('name').alignment = { vertical: 'middle', wrapText: true };
        // Money withheld is shown red, matching the sheet's own export.
        deductionKeys.forEach(k => { row.getCell(k).font = { color: { argb: 'FF991B1B' } }; });
        row.getCell('net').font = { bold: true };
        // On Leave in red, as in the table.
        if (/leave/i.test(status)) row.getCell('status').font = { bold: true, color: { argb: 'FFDC2626' } };
      });

      const total = (pick: (m: MonthSummary) => number) =>
        Math.round(exportRows.reduce((sum, s) => sum + pick(summary[s.id]), 0));

      const totalsRow = worksheet.addRow({
        name: 'TOTAL',
        gross: total(m => m.gross),
        tds: total(m => m.tds),
        pt: total(m => m.pt),
        dedAmt: total(m => m.deductions),
        totalDed: total(m => m.total_deductions),
        net: total(m => m.net_take_home),
      });
      styleDataCells(totalsRow, cell => {
        cell.font = { bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCD34D' } };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      moneyKeys.forEach(key => { worksheet.getColumn(key).numFmt = '#,##0'; });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `${tabLabel} - Salary Register - ${monthLabel} ${selectedYear}.xlsx`);
    } catch (err) {
      console.error('Excel export failed', err);
      setEditError('Could not generate the Excel file.');
    } finally {
      setExporting(false);
    }
  };


  // --- STYLES ---
  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
    titleGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
    title: { margin: 0, fontSize: '1.8rem', fontWeight: 700, color: '#0f172a' },
    subtitle: { margin: 0, color: '#64748b', fontSize: '0.9rem' },
    controlsGroup: { display: 'flex', gap: '1rem', alignItems: 'center' },

    // Tabs
    tabsContainer: { display: 'flex', gap: '1rem', borderBottom: '1px solid #e2e8f0', marginBottom: '1.5rem' },
    tab: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: '#64748b', transition: 'all 0.2s' },
    activeTab: { borderBottom: '2px solid #2563eb', color: '#2563eb' },

    // Filter segmented control
    filterBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' },
    segment: { display: 'inline-flex', backgroundColor: '#eef2f7', borderRadius: '9px', padding: '3px', gap: '2px' },
    segmentBtn: { border: 'none', background: 'transparent', padding: '0.45rem 0.9rem', borderRadius: '7px', fontSize: '0.82rem', fontWeight: 600, color: '#64748b', cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' },
    segmentBtnActive: { backgroundColor: 'white', color: '#0f172a', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' },
    filterNote: { fontSize: '0.8rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.4rem' },
    // Totals cards — same look as the Salary Sheet's header cards.
    headStat: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0.28rem 0.5rem', borderRadius: '8px', backgroundColor: 'white', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', minWidth: '78px', whiteSpace: 'nowrap' },
    headStatSelected: { borderColor: '#93c5fd', backgroundColor: '#eff6ff' },
    headStatLabel: { fontSize: '0.58rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' },
    headStatValue: { fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.2 },
    linkBtn: { border: 'none', background: 'none', padding: 0, color: '#2563eb', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' },
    exportBtn: { display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.5rem 0.9rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' },
    banner: { padding: '0.6rem 0.9rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 500, marginBottom: '1rem' },

    // Date Picker & Search
    datePickerContainer: { display: 'flex', alignItems: 'center', backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '0.2rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', position: 'relative' },
    pickerBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.8rem', backgroundColor: 'transparent', border: 'none', fontSize: '0.9rem', fontWeight: 600, color: '#334155', cursor: 'pointer', borderRadius: '8px', outline: 'none', minWidth: '110px', justifyContent: 'space-between' },
    divider: { width: '1px', height: '20px', backgroundColor: '#e2e8f0', margin: '0 2px' },
    dropdownMenu: { position: 'absolute', top: 'calc(100% + 8px)', left: 0, backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '150px', maxHeight: '300px', overflowY: 'auto', zIndex: 50, padding: '0.3rem' },
    dropdownItem: { padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: '#475569', cursor: 'pointer', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'background 0.1s' },
    activeItem: { backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 600 },
    searchContainer: { position: 'relative', display: 'flex', alignItems: 'center' },
    searchInput: { padding: '0.6rem 1rem 0.6rem 2.5rem', borderRadius: '10px', border: '1px solid #e2e8f0', outline: 'none', minWidth: '220px', fontSize: '0.9rem', backgroundColor: 'white', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    searchIcon: { position: 'absolute', left: '10px', color: '#94a3b8' },

    // Table
    tableCard: { backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #cbd5e1', overflow: 'hidden' },
    tableWrapper: { overflowX: 'auto' },
    // Fixed layout so the widths set on the header cells are honoured exactly.
    // Each width is sized to that column's longest real value — "Class 2
    // (Tracheostomy ICU Nurses)", "Available For Shuffle", "Registered Nurse" —
    // so nothing is cut off at the minimum width. minWidth is the sum of them,
    // so a narrow screen scrolls instead of squeezing; anything wider than that
    // spreads across all the columns proportionally rather than into the name.
    table: { width: '100%', minWidth: `${TABLE_MIN_WIDTH}px`, tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left', color: '#334155' },
    thead: { backgroundColor: '#e2e8f0', borderBottom: '2px solid #94a3b8' },
    th: { padding: '0.7rem 0.4rem', color: '#334155', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', textAlign: 'center', border: '1px solid #cbd5e1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    tbody: { backgroundColor: 'white' },
    // Everything centred; Staff Name overrides this to left.
    td: { padding: '0.45rem 0.4rem', border: '1px solid #cbd5e1', verticalAlign: 'middle', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    editTd: { padding: '0.2rem 0.15rem', border: '1px solid #cbd5e1', verticalAlign: 'middle', textAlign: 'center' },

    // Components
    avatar: { width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #cbd5e1', marginRight: '0.5rem', flexShrink: 0 },
    nameCell: { display: 'flex', alignItems: 'center', minWidth: 0 },
    empId: { fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 },
    statusPill: { display: 'inline-flex', alignItems: 'center', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600, lineHeight: 1, border: '1px solid rgba(0,0,0,0.1)' },
    sheetBtn: { backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', padding: '0.4rem 0.5rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem', justifyContent: 'center', transition: 'all 0.2s', width: '100%' },
    boldText: { fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  };

  const isNurseTab = activeTab === 'nurse';
  // #, Staff Name, Contact, Status, Action — plus Designation and Clinical
  // Class (nurse tab only) and Role/Category, when shown.
  const columnCount = 5 + PAY_COLUMNS.length
    + (isNurseTab ? 1 + (SHOW_CLINICAL_CLASS ? 1 : 0) : 0)
    + (SHOW_ROLE ? 1 : 0);

  // While the summary is still in flight every row looks unassigned, so an
  // Assigned/Not-assigned view would flash "nobody was assigned" before the
  // real answer lands. Keep showing the loading row until it does.
  const tableLoading = loading || (supportsAssignment && summaryLoading && assignmentFilter !== 'all');

  return (
    <div style={styles.page}>
      {/* Row hover is CSS rather than React state: hovering a row used to set
          state and re-render the whole table on every mouse move between rows. */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .salary-index-row { transition: background-color 0.1s; }
        .salary-index-row:hover { background-color: #f8fafc; }
      `}</style>

      <header style={styles.header}>
        <div style={styles.titleGroup}>
            <h1 style={styles.title}>Salary Sheets</h1>
            <p style={styles.subtitle}>
              {supportsAssignment
                ? `Staff assigned in ${monthLabel} ${selectedYear}.`
                : 'Select a category to manage staff payroll.'}
            </p>
        </div>

        <div style={styles.controlsGroup}>
            {/* Custom Date Picker */}
            <div style={styles.datePickerContainer}>
                <div style={{position: 'relative'}} ref={monthRef}>
                    <button style={styles.pickerBtn} onClick={() => { setIsMonthOpen(!isMonthOpen); setIsYearOpen(false); }}>
                        <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                           <Calendar size={15} color="#3b82f6" />
                           <span>{monthLabel}</span>
                        </div>
                        <ChevronDown size={14} color="#94a3b8" style={{transform: isMonthOpen ? 'rotate(180deg)' : 'rotate(0deg)'}}/>
                    </button>
                    {isMonthOpen && (
                        <div style={styles.dropdownMenu}>
                            {months.map((m) => (
                                <div key={m.val} style={{...styles.dropdownItem, ...(m.val === selectedMonth ? styles.activeItem : {})}} onClick={() => { setSelectedMonth(m.val); setIsMonthOpen(false); }}>
                                    {m.label} {m.val === selectedMonth && <Check size={14} />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div style={styles.divider}></div>
                <div style={{position: 'relative'}} ref={yearRef}>
                    <button style={{...styles.pickerBtn, minWidth: '90px'}} onClick={() => { setIsYearOpen(!isYearOpen); setIsMonthOpen(false); }}>
                        <span>{selectedYear}</span>
                        <ChevronDown size={14} color="#94a3b8" style={{transform: isYearOpen ? 'rotate(180deg)' : 'rotate(0deg)'}}/>
                    </button>
                    {isYearOpen && (
                        <div style={{...styles.dropdownMenu, width: '100px', left: 'auto', right: 0}}>
                            {years.map((y) => (
                                <div key={y} style={{...styles.dropdownItem, ...(y === selectedYear ? styles.activeItem : {})}} onClick={() => { setSelectedYear(y); setIsYearOpen(false); }}>
                                    {y} {y === selectedYear && <Check size={14} />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Search */}
            <div style={styles.searchContainer}>
                <Search size={16} style={styles.searchIcon} />
                <input
                    type="text"
                    placeholder={`Search ${activeTab}s, numbers, amounts...`}
                    style={styles.searchInput}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Sits in the header rather than the filter bar so it is available
                on the Management tab too, which has no assignment filter. */}
            <button
                ref={exportBtnRef}
                onClick={handleExportExcel}
                disabled={exporting || exportRows.length === 0}
                title={exportRows.length === 0
                  ? `No saved payroll for ${monthLabel} ${selectedYear}`
                  : `Download ${selectedIds.size ? 'the selected' : 'these'} ${exportRows.length} staff as an Excel register`}
                style={{
                  ...styles.exportBtn,
                  cursor: (exporting || exportRows.length === 0) ? 'not-allowed' : 'pointer',
                  opacity: (exporting || exportRows.length === 0) ? 0.5 : 1,
                }}
            >
                {exporting
                  ? <><Loader2 size={15} style={{animation: 'spin 1s linear infinite'}} /> Preparing…</>
                  : <><Download size={15} /> Excel ({exportRows.length})</>}
            </button>
        </div>
      </header>

      {/* --- TABS --- */}
      <div style={styles.tabsContainer}>
          <button
            style={{...styles.tab, ...(activeTab === 'nurse' ? styles.activeTab : {})}}
            onClick={() => setActiveTab('nurse')}
          >
              <Users size={18} /> Nursing Staff
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'physio' ? styles.activeTab : {})}}
            onClick={() => setActiveTab('physio')}
          >
              <Stethoscope size={18} /> Physiotherapists
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'management' ? styles.activeTab : {})}}
            onClick={() => setActiveTab('management')}
          >
              <Briefcase size={18} /> Management
          </button>
      </div>

      {/* --- ASSIGNMENT FILTER + TOTALS --- */}
      {!summaryFailed && (
        <div style={styles.filterBar}>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap'}}>
            {supportsAssignment && (
              <div style={styles.segment}>
                {([
                  { key: 'assigned',   label: `Assigned (${counts.assigned})` },
                  { key: 'unassigned', label: `Not assigned (${counts.unassigned})` },
                  { key: 'all',        label: `All (${counts.all})` },
                ] as { key: AssignmentFilter; label: string }[]).map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setAssignmentFilter(opt.key)}
                    style={{...styles.segmentBtn, ...(assignmentFilter === opt.key ? styles.segmentBtnActive : {})}}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* Same six figures as a Salary Sheet's header cards, summed over
                the selected rows — or every row shown when nothing is selected. */}
            <div ref={totalsRef} style={{display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap'}}>
              {PAY_COLUMNS.map((c, i) => (
                <div key={c.key} style={{...styles.headStat, ...(selectedRows.length ? styles.headStatSelected : {})}}>
                  <span style={styles.headStatLabel}>{c.label}</span>
                  <span style={{...styles.headStatValue, color: c.color}}>₹ {payTotals[i].toLocaleString('en-IN')}</span>
                </div>
              ))}
              <span style={styles.filterNote}>
                {selectedRows.length ? (
                  <>
                    {selectedRows.length} selected ·
                    <button type="button" onClick={() => setSelectedIds(new Set())} style={styles.linkBtn}>Clear</button>
                  </>
                ) : `All ${orderedStaff.length} shown`}
              </span>
            </div>
          </div>
          {supportsAssignment && (
            <div style={styles.filterNote}>
              {summaryLoading
                ? <><Loader2 size={13} style={{animation: 'spin 1s linear infinite'}} /> Loading {monthLabel} duties…</>
                : <>Assigned = has at least one shift on the salary sheet for {monthLabel} {selectedYear}.</>}
            </div>
          )}
        </div>
      )}

      {summaryFailed && (
        <div style={{...styles.banner, backgroundColor: '#fffbeb', color: '#92400e', border: '1px solid #fde68a'}}>
          Couldn't load duty data for {monthLabel} {selectedYear} — showing all staff, and the month columns are blank.
        </div>
      )}

      {editError && (
        <div style={{...styles.banner, backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca'}}>
          {editError}
        </div>
      )}

      {/* --- TABLE --- */}
      <div ref={tableCardRef} style={styles.tableCard}>
        <div style={styles.tableWrapper}>
            <table style={styles.table}>
                <thead style={styles.thead}>
                    <tr>
                        <th style={{...styles.th, width: COL.index}}>#</th>
                        <th style={{...styles.th, width: COL.name, textAlign: 'left'}}>Staff Name</th>
                        {isNurseTab && <th style={{...styles.th, width: COL.designation}}>Designation</th>}
                        {isNurseTab && SHOW_CLINICAL_CLASS && <th style={{...styles.th, width: COL.clinicalClass}}>Clinical Class</th>}
                        {SHOW_ROLE && <th style={{...styles.th, width: COL.role}}>Role / Category</th>}
                        <th style={{...styles.th, width: COL.contact}}>Contact</th>
                        <th style={{...styles.th, width: COL.status}}>Status</th>
                        {PAY_COLUMNS.map(c => (
                          <th key={c.key} title={c.label} style={{...styles.th, width: COL.money, whiteSpace: 'normal', lineHeight: 1.2}}>{c.label}</th>
                        ))}
                        <th style={{...styles.th, width: COL.action}}>Action</th>
                    </tr>
                </thead>
                <tbody style={styles.tbody}>
                    {tableLoading ? (
                         <tr><td colSpan={columnCount} style={{padding: '3rem', textAlign: 'center', color: '#64748b'}}>Loading data...</td></tr>
                    ) : orderedStaff.length === 0 ? (
                        <tr><td colSpan={columnCount} style={{padding: '3rem', textAlign: 'center', color: '#94a3b8'}}>
                          {supportsAssignment && assignmentFilter === 'assigned' && counts.all > 0
                            ? `No ${activeTab}s were assigned any duty in ${monthLabel} ${selectedYear}.`
                            : 'No staff found matching filters.'}
                        </td></tr>
                    ) : (
                        orderedStaff.map((staff, index) => (
                            <tr
                              key={staff.id}
                              className="salary-index-row"
                              style={selectedIds.has(staff.id) ? { backgroundColor: '#dbeafe' } : undefined}
                              onMouseEnter={() => selectThrough(index)}
                            >
                              <td style={{...styles.td, textAlign: 'center', backgroundColor: selectedIds.has(staff.id) ? '#bfdbfe' : '#f8fafc'}}>{index + 1}</td>

                              <td
                                style={{...styles.td, textAlign: 'left', cursor: 'pointer', userSelect: 'none'}}
                                onMouseDown={(e) => startSelect(index, e)}
                                title="Click to select · drag over names to select several · Esc to clear"
                              >
                                  <div style={styles.nameCell}>
                                      <img src={getImageUrl(staff.photo_url)} alt="" style={styles.avatar} onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}/>
                                      <div style={{display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0}}>
                                        <span style={styles.boldText} title={staff.full_name}>{staff.full_name}</span>
                                        {staff.emp_id && <span style={styles.empId} title={staff.emp_id}>{staff.emp_id}</span>}
                                      </div>
                                  </div>
                              </td>

                              {isNurseTab && (
                                <td style={styles.editTd}>
                                  <EditableCell
                                    value={staff.designation || ''}
                                    options={DESIGNATION_OPTIONS}
                                    placeholder="Set designation"
                                    busy={savingCell === `${staff.id}_designation`}
                                    onSelect={(v) => handleInlineEdit(staff, 'designation', v)}
                                  />
                                </td>
                              )}
                              {isNurseTab && SHOW_CLINICAL_CLASS && (
                                <td style={styles.editTd}>
                                  <EditableCell
                                    value={staff.clinical_class || ''}
                                    options={CLINICAL_CLASS_OPTIONS}
                                    placeholder="Set clinical class"
                                    busy={savingCell === `${staff.id}_clinical_class`}
                                    onSelect={(v) => handleInlineEdit(staff, 'clinical_class', v)}
                                  />
                                </td>
                              )}

                              {SHOW_ROLE && <td style={styles.td} title={staff.role_display}>{staff.role_display}</td>}

                              <td style={styles.td} title={staff.contact_display !== '-' ? staff.contact_display : undefined}>
                                  {/* One line per registered number. */}
                                  {staff.contact_numbers.length ? staff.contact_numbers.map(n => (
                                      <div key={n} style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'0.25rem', fontSize:'0.85rem', overflow:'hidden', lineHeight: 1.4}}>
                                          <Phone size={12} color="#64748b" style={{flexShrink: 0}}/>
                                          <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{n}</span>
                                      </div>
                                  )) : '-'}
                              </td>

                              <td style={isNurseTab ? styles.editTd : {...styles.td, textAlign: 'center'}}>
                                  {isNurseTab ? (
                                    <EditableCell
                                      value={liveStatus[staff.id] || 'Available'}
                                      options={STAFF_STATUS_OPTIONS}
                                      placeholder="Set status"
                                      color={statusTextColor(liveStatus[staff.id] || 'Available')}
                                      busy={savingCell === `${staff.id}_staff_status`}
                                      onSelect={(v) => handleInlineEdit(staff, 'staff_status', v)}
                                    />
                                  ) : (
                                    <span style={{...styles.statusPill, ...getStatusStyle(staff.staff_status)}} title={staff.staff_status}>
                                        {staff.staff_status}
                                    </span>
                                  )}
                              </td>

                              {/* No summary row = nothing on the sheet this month. */}
                              {PAY_COLUMNS.map(c => {
                                const m = summary[staff.id];
                                const v = m ? Math.round(Number(m[c.key]) || 0) : null;
                                return (
                                  <td key={c.key} style={{...styles.td, fontVariantNumeric: 'tabular-nums', fontWeight: c.key === 'net_take_home' ? 700 : 600, color: v === null ? '#cbd5e1' : c.color}}>
                                    {v === null ? (summaryLoading ? '…' : '–') : `₹ ${v.toLocaleString('en-IN')}`}
                                  </td>
                                );
                              })}

                              <td style={{...styles.td, textAlign: 'center'}}>
                                  <button
                                      style={styles.sheetBtn}
                                      onClick={() => handleNavigateToSheet(staff.id, staff.type)}
                                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dcfce7'}
                                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                                  >
                                      <FileText size={16} /> Open Sheet
                                  </button>
                              </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
      </div>
    </div>
  );
};

export default SalarySheetIndex;
